import React from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormHelperText,
  Grid,
} from '@mui/material';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import {
  CompanyRegistrationData,
  BusinessType,
  EmployeeCountRange,
} from '../../types/company';
import { PREFECTURES } from '../../constants/prefectures';
import { BUSINESS_TYPES, EMPLOYEE_COUNT_RANGES } from '../../constants/company';

interface CompanyInfoStepProps {
  register: UseFormRegister<CompanyRegistrationData>;
  errors: FieldErrors<CompanyRegistrationData>;
  watchedValues: Partial<CompanyRegistrationData>;
  onFieldFocus: (fieldName: keyof CompanyRegistrationData) => void;
}

export const CompanyInfoStep: React.FC<CompanyInfoStepProps> = ({
  register,
  errors,
  watchedValues,
  onFieldFocus,
}) => {
  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
        企業情報
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        企業の基本情報を入力してください。
      </Typography>

      <Grid container spacing={3}>
        {/* 企業名 */}
        <Grid item xs={12}>
          <TextField
            {...register('companyName')}
            label="企業名"
            fullWidth
            required
            placeholder="例: 株式会社○○不動産"
            error={!!errors.companyName}
            helperText={errors.companyName?.message}
            onFocus={() => onFieldFocus('companyName')}
            inputProps={{ 'data-testid': 'company-name' }}
          />
        </Grid>

        {/* 代表者氏名 */}
        <Grid item xs={12}>
          <TextField
            {...register('representativeName')}
            label="代表者氏名"
            fullWidth
            required
            placeholder="例: 山田 太郎"
            error={!!errors.representativeName}
            helperText={errors.representativeName?.message}
            onFocus={() => onFieldFocus('representativeName')}
            inputProps={{ 'data-testid': 'representative-name' }}
          />
        </Grid>

        {/* 所在地 */}
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('prefecture')}
            select
            label="都道府県"
            fullWidth
            required
            value={watchedValues.prefecture || ''}
            error={!!errors.prefecture}
            helperText={errors.prefecture?.message}
            onFocus={() => onFieldFocus('prefecture')}
            inputProps={{ 'data-testid': 'prefecture' }}
          >
            <MenuItem value="">選択してください</MenuItem>
            {PREFECTURES.map((prefecture) => (
              <MenuItem key={prefecture.code} value={prefecture.name}>
                {prefecture.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            {...register('city')}
            label="市区町村"
            fullWidth
            required
            placeholder="例: 新宿区"
            error={!!errors.city}
            helperText={errors.city?.message}
            onFocus={() => onFieldFocus('city')}
            inputProps={{ 'data-testid': 'city' }}
          />
        </Grid>

        {/* 業種 */}
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('businessType')}
            select
            label="業種"
            fullWidth
            required
            value={watchedValues.businessType || ''}
            error={!!errors.businessType}
            helperText={errors.businessType?.message}
            onFocus={() => onFieldFocus('businessType')}
            inputProps={{ 'data-testid': 'business-type' }}
          >
            <MenuItem value="">選択してください</MenuItem>
            {BUSINESS_TYPES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* 従業員数規模 */}
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('employeeCountRange')}
            select
            label="従業員数規模"
            fullWidth
            required
            value={watchedValues.employeeCountRange || ''}
            error={!!errors.employeeCountRange}
            helperText={errors.employeeCountRange?.message}
            onFocus={() => onFieldFocus('employeeCountRange')}
            inputProps={{ 'data-testid': 'employee-count' }}
          >
            <MenuItem value="">選択してください</MenuItem>
            {EMPLOYEE_COUNT_RANGES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Box>
  );
};
